/**
 * Right rail Style Cart panel for the POS Styles Workshop layout.
 *
 * Shows a running summary of every value the user has picked across the
 * jacket / pant / vest / shirt styling sections, with their factory
 * rc-code badge next to each value. Updates live as state changes.
 *
 * Renders only on ≥2xl screens (1536px+); hidden on smaller screens so the
 * existing single-column layout remains untouched.
 *
 * Props:
 *   jacketStyling, pantStyling, vestStyling, shirtStyling   — current state
 *   includes        { jacket, trouser, vest, shirt }
 *   fabricSelection lining selection, etc. (optional, displayed in footer)
 *   estimatedPrice  number — final price, shown at the bottom
 *   make, constructionType  — make summary chip
 */
import { getStyleCode } from "@/data/styleCodes";
import { MAKE_PROFILES } from "@/lib/makePricing";

// Pretty-print a snake_case field name. Mirrors the same display logic the
// styling cards use elsewhere so the cart stays in sync visually.
const PRETTY = {
  body_style: "Body",
  canvas_construction: "Canvas",
  front_closure: "Closure",
  lapel_type: "Lapel",
  lapel_width: "Lapel W.",
  waist_pocket_type: "Waist Pkt",
  waist_pocket_angle: "Pkt Angle",
  ticket_pocket: "Ticket Pkt",
  chest_pocket: "Chest Pkt",
  vent: "Vent",
  button_style: "Buttons",
  exterior_stitch: "Ext. Stitch",
  sleeve_buttonholes: "Sleeve Buttons",
  lapel_buttonhole: "Lapel BH",
  gorge_line: "Gorge",
  lining_style: "Lining",
  chest_pocket_posture: "Chest Pkt Pos.",
  lower_pocket_flap_width: "Pkt Flap W.",
  ticket_pocket_posture: "Ticket Pkt Pos.",
  back_vent_length: "Vent Length",
  armhole_adjustment: "Armhole Adj.",
  shoulder_posture_adjustment: "Shoulder Adj.",
  // Pant
  pant_body_style: "Body",
  pant_front_style: "Front Style",
  pant_front_pocket: "Front Pkt",
  pant_back_pocket: "Back Pkt",
  pant_bottom_finishing: "Bottom",
  pant_waistband_style: "Waistband",
  pant_front_closure: "Closure",
  // Vest
  vest_lapel: "Lapel",
  vest_front_closure: "Closure",
  vest_pocket_type: "Pocket",
  vest_pocket_number: "Pkt Count",
  vest_back: "Back",
  // Shirt
  shirt_collar_style: "Collar",
  shirt_cuff_style: "Cuffs",
  shirt_pocket: "Pocket",
  shirt_back: "Back",
};

function prettyLabel(field) {
  if (PRETTY[field]) return PRETTY[field];
  return field
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Values that count as "no choice" — skip in the cart so we don't clutter
// the panel with default Normal / Standard / None entries the user didn't
// explicitly pick.
const SKIP_VALUES = new Set(["", "Normal", "Standard", "None", "—", "default", "Default"]);

function meaningful(value) {
  if (value === null || value === undefined) return false;
  const s = String(value).trim();
  if (!s) return false;
  if (SKIP_VALUES.has(s)) return false;
  return true;
}

function GarmentBlock({ label, color, styling, fieldPrefix = "", testIdPrefix }) {
  if (!styling || typeof styling !== "object") return null;
  const entries = Object.entries(styling).filter(([k, v]) => {
    if (!meaningful(v)) return false;
    // Skip remarks/notes/free-text fields
    if (k.endsWith("_remarks") || k.endsWith("_notes") || k === "notes") return false;
    return true;
  });
  if (entries.length === 0) return null;

  const colorClasses = {
    amber:  "text-amber-300 border-amber-500/30",
    blue:   "text-blue-300 border-blue-500/30",
    purple: "text-purple-300 border-purple-500/30",
    rose:   "text-rose-300 border-rose-500/30",
  };
  const headerColor = colorClasses[color] || colorClasses.amber;

  return (
    <div className={`pb-2 mb-2 border-b ${headerColor}`} data-testid={testIdPrefix}>
      <p className={`text-[11px] uppercase tracking-widest font-semibold ${headerColor.split(" ")[0]} mb-1.5`}>
        {label}
        <span className="ml-1.5 text-slate-500 font-normal">· {entries.length}</span>
      </p>
      <ul className="space-y-1">
        {entries.map(([k, v]) => {
          // Resolve the rc-code using the same field prefix the dropdown uses.
          // Pant fields are stored under bare keys ("body_style", "front_pocket")
          // while the rc_codes lookup uses prefixed keys ("pant_body_style").
          // Try the prefixed key first; if no match, fall back to the bare key.
          let code = "";
          if (fieldPrefix && !k.startsWith(fieldPrefix)) {
            code = getStyleCode(`${fieldPrefix}${k}`, [], v);
          }
          if (!code) code = getStyleCode(k, [], v);
          return (
            <li key={k} className="flex items-start gap-2 text-[11px] leading-snug" data-testid={`${testIdPrefix}-row-${k}`}>
              <span className="text-slate-500 shrink-0 min-w-[78px]">{prettyLabel(k)}</span>
              <span className="text-slate-200 flex-1 truncate" title={String(v)}>{String(v)}</span>
              {code && (
                <code className="text-[10px] text-amber-400/90 font-mono shrink-0" data-testid={`${testIdPrefix}-code-${k}`}>{code}</code>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default function StyleCartPanel({
  jacketStyling,
  pantStyling,
  vestStyling,
  shirtStyling,
  includes = {},
  fabricSelection = null,
  estimatedPrice = null,
  make = "rc",
  constructionType = "",
  className = "",
}) {
  const totalPicks =
    (includes.jacket  ? Object.values(jacketStyling || {}).filter(meaningful).length  : 0) +
    (includes.trouser ? Object.values(pantStyling || {}).filter(meaningful).length    : 0) +
    (includes.vest    ? Object.values(vestStyling || {}).filter(meaningful).length    : 0) +
    (includes.shirt   ? Object.values(shirtStyling || {}).filter(meaningful).length   : 0);

  return (
    <aside
      data-testid="pos-workshop-style-cart"
      className={`hidden 2xl:flex flex-col sticky top-4 self-start max-h-[calc(100vh-2rem)] overflow-y-auto rounded-xl bg-[#111c2e] border border-white/10 ${className}`}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-white/10 bg-[#0a1628]/60 sticky top-0 z-10 backdrop-blur">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-slate-100">Style Cart</p>
          <span className="text-[10px] uppercase tracking-widest text-slate-500">{totalPicks} picks</span>
        </div>
        <p className="text-[11px] text-slate-500 mt-0.5">Live summary of every styling field</p>
      </div>

      {/* Body */}
      <div className="px-4 py-3 flex-1">
        {totalPicks === 0 && (
          <p className="text-xs text-slate-500 italic">
            Start picking styles in the center pane — your selections appear here with their factory codes.
          </p>
        )}
        {includes.jacket  && <GarmentBlock label="Jacket"  color="amber"  styling={jacketStyling} fieldPrefix=""        testIdPrefix="cart-jacket"  />}
        {includes.trouser && <GarmentBlock label="Trouser" color="blue"   styling={pantStyling}   fieldPrefix="pant_"   testIdPrefix="cart-pant"    />}
        {includes.vest    && <GarmentBlock label="Vest"    color="purple" styling={vestStyling}   fieldPrefix="vest_"   testIdPrefix="cart-vest"    />}
        {includes.shirt   && <GarmentBlock label="Shirt"   color="rose"   styling={shirtStyling}  fieldPrefix="shirt_"  testIdPrefix="cart-shirt"   />}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-white/10 bg-[#0a1628]/60">
        {fabricSelection && (
          <div className="flex items-center justify-between text-[11px] mb-2">
            <span className="text-slate-500">Fabric</span>
            <span className="text-slate-200 truncate ml-2" title={fabricSelection.rc_code || fabricSelection.name || ""}>
              {fabricSelection.rc_code || fabricSelection.name || "—"}
            </span>
          </div>
        )}
        <div className="flex items-center justify-between text-[11px] mb-2">
          <span className="text-slate-500">Make</span>
          <span className="font-medium" style={{ color: MAKE_PROFILES[make]?.color || "#3b82f6" }}>
            {MAKE_PROFILES[make]?.label || "RC Make"} · {constructionType || "—"}
          </span>
        </div>
        {estimatedPrice !== null && estimatedPrice !== undefined && (
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
            <span className="text-[11px] uppercase tracking-widest text-slate-500">Est. Total</span>
            <span className="text-lg font-bold text-emerald-400" data-testid="cart-estimated-price">
              ${Math.round(estimatedPrice).toLocaleString()}
            </span>
          </div>
        )}
      </div>
    </aside>
  );
}
